import type { Tone } from '@/design-system';
import { isRole, type Role } from '@/shared/api';

import type { InvitationResource, UserResource } from './types';

/* -------------------------------------------------------------------------- */
/* Lidmaatschappen: één persoon, meerdere keuringsbedrijven                    */
/* -------------------------------------------------------------------------- */

/**
 * Een keurmeester kan bij meerdere keuringsbedrijven horen. Er is dus één
 * globale gebruiker en per bedrijf een **lidmaatschap** met een eigen rol en
 * status.
 *
 * Wat een bedrijf in Instellingen ziet, zijn de mensen met een lidmaatschap van
 * *dit* bedrijf - nooit een spoor van de andere bedrijven waar iemand werkt. Dat
 * is bedrijfsgevoelig: alleen het platformbeheer heeft dat beeld.
 */
export const MEMBERSHIP_STATUSES = ['active', 'invited', 'disabled'] as const;

export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/** Gelijk aan `MembershipStatus::label()` in de API. */
export const MEMBERSHIP_STATUS_LABELS: Record<MembershipStatus, string> = {
    active: 'Actief',
    invited: 'Uitgenodigd',
    disabled: 'Uitgeschakeld',
};

/** Actief is groen, uitgenodigd blauw, uitgeschakeld neutraal - nooit rood. */
export const MEMBERSHIP_STATUS_TONES: Record<MembershipStatus, Tone> = {
    active: 'accent',
    invited: 'info',
    disabled: 'neutral',
};

export function membershipStatusOf(value: string | null | undefined): MembershipStatus {
    return (MEMBERSHIP_STATUSES as readonly string[]).includes(value ?? '')
        ? (value as MembershipStatus)
        : 'disabled';
}

/* -------------------------------------------------------------------------- */
/* Rollen                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * De twee rollen binnen een keuringsbedrijf, van meest naar minst verstrekkend
 * (`Role::tenantRoles()` in de API). Beide zijn uit te delen, ook samen: de
 * eigenaar die zelf keurt. De platformrol nooit.
 */
export const TENANT_ROLES = ['owner', 'inspector'] as const;

export type TenantRole = (typeof TENANT_ROLES)[number];

export const ASSIGNABLE_ROLES: readonly TenantRole[] = TENANT_ROLES;

export function isTenantRole(value: string): value is TenantRole {
    return (TENANT_ROLES as readonly string[]).includes(value);
}

/**
 * De rol die op locatie werkt: de inspecteur. Wie die rol heeft, staat op het
 * planbord, heeft Mijn dag en krijgt de eigen opnames op de tablet. Het is ook de
 * enige rol die je jezelf erbij kunt geven (`POST /users/me/roles/inspector`), en
 * alleen als je daarmee niets mag wat je nog niet mocht.
 */
export const FIELD_ROLES = ['inspector'] as const;

export type FieldRole = (typeof FIELD_ROLES)[number];

/** Werkt iemand met deze rollen op locatie? */
export function worksInTheField(roles: readonly string[]): boolean {
    return roles.some((role) => (FIELD_ROLES as readonly string[]).includes(role));
}

/**
 * Wat een rol mag, in gewone taal.
 *
 * Wie rollen uitdeelt moet de gevolgen kunnen zien: deze lijst staat naast de
 * rolkeuze, niet in een helpcentrum. Hij volgt de rechtentabel in
 * `app/Domain/Identity/Enums/Role.php` én de policies daarachter: een inspecteur
 * ziet alleen de keuringen die aan de inspecteur zijn toegewezen
 * (`OrderPolicy::view`), en het planbord is van de eigenaar (`OrderPolicy::plan`).
 */
export const ROLE_CAPABILITIES: Record<Role, readonly string[]> = {
    owner: [
        'Alle keuringen plannen en toewijzen',
        'Rapporten nalezen, goedkeuren en versturen',
        'Facturen versturen, tegoed opwaarderen, tarieven en sjablonen beheren',
        'Collega’s uitnodigen, rollen wijzigen en uitschakelen',
        'Huisstijl, domeinen, mailserver en instellingen aanpassen',
    ],
    inspector: [
        'Eigen keuringen opnemen, ook offline',
        'Foto’s en bevindingen vastleggen',
        'Alleen de toegewezen keuringen en hun rapporten inzien',
    ],
    platform_admin: [
        'Alle keuringsbedrijven inzien en beheren',
        'Tegoed handmatig boeken, met verplichte reden',
        'Meekijken als een gebruiker van een bedrijf',
        'Elke handeling van deze rol staat in de audit log',
    ],
};

/** Wat er gebeurt als je iemand deze rol geeft, in één zin. */
export const ROLE_SUMMARY: Record<Role, string> = {
    owner: 'Mag alles binnen dit bedrijf, ook rollen, instellingen en tegoed.',
    inspector: 'Werkt op locatie. Ziet alleen de toegewezen keuringen en voert die uit.',
    platform_admin: 'Beheert het platform over alle keuringsbedrijven heen.',
};

/** De meest verstrekkende rol uit een set; die bepaalt wat iemand kan. */
export function primaryRole(roles: readonly Role[]): Role | null {
    return RANK.find((role) => roles.includes(role)) ?? null;
}

const RANK: readonly Role[] = ['platform_admin', 'owner', 'inspector'];

function roleRank(role: Role): number {
    return RANK.indexOf(role);
}

/** Sorteert rollen van meest naar minst verstrekkend. */
export function sortRoles<T extends Role>(roles: readonly T[]): T[] {
    return [...roles].sort((left, right) => roleRank(left) - roleRank(right));
}

/* -------------------------------------------------------------------------- */
/* Rechten                                                                     */
/* -------------------------------------------------------------------------- */

/** De rechten uit de sessie die de schermen van dit bedrijf bepalen. */
export const PERMISSIONS = {
    usersView: 'users.view',
    usersInvite: 'users.invite',
    usersManage: 'users.manage',
    settingsView: 'settings.view',
    settingsManage: 'settings.manage',
    devicesManage: 'devices.manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export function hasPermission(permissions: readonly string[], permission: Permission): boolean {
    return permissions.includes(permission);
}

/* -------------------------------------------------------------------------- */
/* Het team                                                                    */
/* -------------------------------------------------------------------------- */

/** Een collega met een lidmaatschap van dit bedrijf. */
export interface TeamMember {
    /** De persoon; ook de sleutel van `/users/{user}`. */
    uuid: string;
    name: string;
    email: string;
    avatarUrl: string | null;
    /** De status van het lidmaatschap hier. */
    status: MembershipStatus;
    /** De rollen in dit bedrijf, meest verstrekkend eerst. */
    roles: TenantRole[];
    jobTitle: string | null;
    joinedAt: string | null;
    lastActiveAt: string | null;
}

export function toTeamMember(resource: UserResource): TeamMember {
    return {
        uuid: resource.uuid,
        name: resource.name,
        email: resource.email,
        avatarUrl: resource.avatar_url ?? null,
        status: membershipStatusOf(resource.membership?.status),
        roles: sortRoles((resource.roles ?? []).filter(isTenantRole)),
        jobTitle: resource.membership?.job_title ?? null,
        joinedAt: resource.membership?.joined_at ?? null,
        lastActiveAt: resource.membership?.last_active_at ?? resource.last_active_at,
    };
}

/** Een uitnodiging die nog niet is geaccepteerd. */
export interface Invitation {
    uuid: string;
    email: string;
    /** De naam die de uitnodiger opgaf; `null` als hij die niet gaf. */
    name: string | null;
    role: TenantRole | null;
    /** Het label van de API, voor een rol die de app (nog) niet kent. */
    roleLabel: string;
    invitedByName: string | null;
    expiresAt: string;
    expired: boolean;
    createdAt: string | null;
}

export function toInvitation(resource: InvitationResource): Invitation {
    return {
        uuid: resource.uuid,
        email: resource.email,
        name: resource.name?.trim() ? resource.name.trim() : null,
        role: isRole(resource.role) && isTenantRole(resource.role) ? resource.role : null,
        roleLabel: resource.role_label,
        invitedByName: resource.invited_by?.name ?? null,
        expiresAt: resource.expires_at,
        expired: resource.is_expired,
        createdAt: resource.created_at,
    };
}

export function initialsFor(name: string): string {
    const parts = name
        .trim()
        .split(/\s+/)
        .filter((part) => part.length > 0);

    if (parts.length === 0) {
        return '?';
    }

    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

    return `${first}${last}`.toUpperCase();
}
